import { createRouter, createWebHistory } from '@ionic/vue-router';
import { App as CapApp } from '@capacitor/app';
import { tokenStorage } from '@/services/tokenStorage';
import { useCustomerIo } from '@/composables/useCustomerIo';

const isDocsOnly = import.meta.env.VITE_DOCS_ONLY === 'true';

// Documentation routes (always use lazy loading to avoid including in app build)
const docsRoutes = [
  {
    path: '/docs',
    component: () => import('@/views/DocsPage.vue'),
    children: [
      {
        path: '',
        redirect: '/docs/components'
      },
      {
        path: 'components',
        component: () => import('@/views/ComponentsPage.vue')
      },
      {
        path: 'theming',
        component: () => import('@/views/ThemingPage.vue')
      },
      {
        path: 'icons',
        component: () => import('@/views/CustomIconsPage.vue')
      },
    ]
  },
  {
    path: '/docs/accordion',
    component: () => import('@/views/demos/AccordionPage.vue')
  },
  {
    path: '/docs/button',
    component: () => import('@/views/demos/ButtonPage.vue')
  },
  {
    path: '/docs/card',
    component: () => import('@/views/demos/CardPage.vue')
  },
  {
    path: '/docs/checkbox',
    component: () => import('@/views/demos/CheckboxPage.vue')
  },
  {
    path: '/docs/icon',
    component: () => import('@/views/demos/IconPage.vue')
  },
  {
    path: '/docs/infobox',
    component: () => import('@/views/demos/InfoBoxPage.vue')
  },
  {
    path: '/docs/input',
    component: () => import('@/views/demos/InputPage.vue')
  },
  {
    path: '/docs/search',
    component: () => import('@/views/demos/SearchPage.vue')
  },
  {
    path: '/docs/segment',
    component: () => import('@/views/demos/SegmentPage.vue')
  },
  {
    path: '/docs/tabs',
    component: () => import('@/views/demos/TabsDemoPage.vue')
  },
  {
    path: '/docs/text',
    component: () => import('@/views/demos/TextPage.vue')
  },
  {
    path: '/docs/toggle',
    component: () => import('@/views/demos/TogglePage.vue')
  },
  {
    path: '/docs/toolbar',
    component: () => import('@/views/demos/ToolbarPage.vue')
  },
  {
    path: '/docs/colors',
    component: () => import('@/views/demos/ColorsPage.vue')
  },
  {
    path: '/docs/user-avatar',
    component: () => import('@/views/demos/UserAvatarPage.vue')
  },
  {
    path: '/docs/feed',
    component: () => import('@/views/demos/FeedPage.vue')
  },
  {
    path: '/docs/team-badge',
    component: () => import('@/views/demos/TeamBadgeDemo.vue')
  },
];

// Authentication routes (outside tabs, no tab bar visible)
const authRoutes = [
  {
    path: '/auth',
    component: () => import('@/views/StartPage.vue')
  },
  {
    path: '/auth/signin',
    component: () => import('@/views/auth/AuthPage.vue')
  },
  {
    path: '/auth/verification',
    component: () => import('@/views/auth/VerificationPage.vue')
  },
];

// Onboarding routes (outside tabs, no tab bar visible)
const onboardingRoutes = [
  {
    path: '/onboarding',
    redirect: '/onboarding/sports'
  },
  {
    path: '/onboarding/sports',
    component: () => import('@/views/onboarding/SportsPage.vue')
  },
  {
    path: '/onboarding/markets',
    component: () => import('@/views/onboarding/MarketsPage.vue')
  },
  {
    path: '/onboarding/bet-type',
    component: () => import('@/views/onboarding/BetTypePage.vue')
  },
  {
    path: '/onboarding/bet-timing',
    component: () => import('@/views/onboarding/BetTimingPage.vue')
  },
  {
    path: '/onboarding/odds',
    component: () => import('@/views/onboarding/OddsPage.vue')
  },
  {
    path: '/onboarding/notifications',
    component: () => import('@/views/onboarding/NotificationsPage.vue')
  },
  {
    path: '/onboarding/congratulations',
    component: () => import('@/views/onboarding/CongratulationsPage.vue')
  },
];

// App routes (excluded in docs-only mode)
const appRoutes = isDocsOnly ? [] : [
  {
    path: '/tabs/',
    component: () => import('@/views/TabsPage.vue'),
    children: [
      {
        path: '',
        redirect: '/tabs/feed'
      },
      {
        path: 'fixtures',
        component: () => import('@/views/FixturesPage.vue')
      },
      {
        path: 'menu',
        component: () => import('@/views/MenuPage.vue')
      },
      {
        path: 'feed',
        component: () => import('@/views/FeedPage.vue')
      },
      {
        path: 'profile',
        component: () => import('@/views/ProfilePage.vue')
      },
      {
        path: 'search',
        component: () => import('@/views/SearchPage.vue')
      },
      {
        path: 'notifications',
        component: () => import('@/views/NotificationsPage.vue')
      },
      {
        path: 'widget-bet-of-the-day/:id',
        component: () => import('@/views/widgets/WidgetBetOfTheDayPage.vue')
      },
      {
        path: 'widget-best-community-tip/:id',
        component: () => import('@/views/widgets/WidgetBestCommunityTipPage.vue')
      },
      {
        path: 'widget-accumulators/:id',
        component: () => import('@/views/widgets/WidgetAccumulatorsPage.vue')
      },
      {
        path: 'widget-dropping-odds-tips/:id',
        component: () => import('@/views/widgets/WidgetDroppingOddsTipsPage.vue')
      },
      {
        path: 'widget-live-bet-alert/:id',
        component: () => import('@/views/widgets/WidgetLiveBetAlertPage.vue')
      },
      {
        path: 'widget-trend-of-the-day/:id',
        component: () => import('@/views/widgets/WidgetTrendOfTheDayPage.vue')
      },
      {
        path: 'widget-stats-alert/:id',
        component: () => import('@/views/widgets/WidgetStatsAlertPage.vue')
      },
      {
        path: 'widget-daily-banker/:id',
        component: () => import('@/views/widgets/WidgetDailyBankerPage.vue')
      },
      {
        path: 'widget-daily-value-bet/:id',
        component: () => import('@/views/widgets/WidgetDailyValueBetPage.vue')
      },
      {
        path: 'widget-trending-tipster/:id',
        component: () => import('@/views/widgets/WidgetTrendingTipsterPage.vue')
      },
      {
        path: 'widget-live-expert-tip/:id',
        component: () => import('@/views/widgets/WidgetLiveExpertTipPage.vue')
      },
      //
      {
        path: 'widget-tips-from-tipster/:id',
        component: () => import('@/views/widgets/WidgetTipsFromTipsterPage.vue')
      }
    ]
  },
  // Profile routes (outside tabs for full-page navigation)
  {
    path: '/profile/account',
    component: () => import('@/views/profile/AccountPage.vue')
  },
  {
    path: '/profile/account/country',
    component: () => import('@/views/profile/account/CountryPage.vue')
  },
  {
    path: '/profile/account/delete-verify',
    component: () => import('@/views/profile/account/DeleteAccountVerificationPage.vue')
  },
  {
    path: '/profile/account/change-email/verify-old',
    component: () => import('@/views/profile/account/ChangeEmailVerifyOldPage.vue')
  },
  {
    path: '/profile/account/change-email/new',
    component: () => import('@/views/profile/account/ChangeEmailNewPage.vue')
  },
  {
    path: '/profile/account/change-email/verify-new',
    component: () => import('@/views/profile/account/ChangeEmailVerifyNewPage.vue')
  },
  {
    path: '/profile/content',
    component: () => import('@/views/profile/ContentPage.vue')
  },
  {
    path: '/profile/notifications',
    component: () => import('@/views/profile/NotificationsPage.vue')
  },
  {
    path: '/profile/preferences',
    component: () => import('@/views/profile/PreferencesPage.vue')
  },
  {
    path: '/profile/preferences/timezone',
    component: () => import('@/views/profile/preferences/TimezonePage.vue')
  },
  {
    path: '/profile/preferences/language',
    component: () => import('@/views/profile/preferences/LanguagePage.vue')
  },
  {
    path: '/profile/revenuecat-debug',
    component: () => import('@/views/profile/RevenueCatDebugPage.vue')
  },
  {
    path: '/profile/about',
    component: () => import('@/views/profile/AboutPage.vue')
  },
  {
    path: '/purchase-success',
    component: () => import('@/views/PurchaseSuccessPage.vue')
  },
  {
    path: '/fixture/:id',
    redirect: (to) => `/fixture/${to.params.id}/overview`
  },
  {
    path: '/fixture/:id/:tab',
    component: () => import('@/views/fixture/FixturePage.vue')
  },
  {
    path: '/:catchAll(.*)',
    redirect: () => '/tabs'
  },
];

const routes = [
  // Root redirect
  {
    path: '/',
    redirect: isDocsOnly ? '/docs' : '/auth'
  },
  // Authentication routes (always available)
  ...authRoutes,
  // Onboarding routes (always available)
  ...onboardingRoutes,
  // Include app routes only if NOT in docs-only mode
  ...appRoutes,
  // Always include documentation routes
  ...docsRoutes
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// Router guard for authentication and auto-login
router.beforeEach(async (to, from, next) => {
  // Skip auth checks in docs-only mode
  if (isDocsOnly) {
    next();
    return;
  }

  // Skip auth checks for docs routes
  if (to.path.startsWith('/docs')) {
    next();
    return;
  }

  const token = await tokenStorage.getAccessToken();
  const refreshToken = await tokenStorage.getRefreshToken();
  const isAuthRoute = to.path.startsWith('/auth');

  // User is NOT logged in
  // Note: We allow missing access token if refresh token exists, since App.vue will refresh it
  if (!refreshToken) {
    // No refresh token means user is truly logged out
    // Clear any leftover access token to avoid broken state
    if (token) {
      await tokenStorage.clearTokens();
    }

    // Allow access to auth pages
    if (isAuthRoute) {
      next();
      return;
    }

    // Redirect to auth page for protected routes
    next('/auth');
    return;
  }

  // User IS logged in
  if (isAuthRoute) {
    // Already logged in, redirect away from auth pages
    // Always redirect to /tabs/feed and let App.vue handle onboarding routing after data loads
    next('/tabs/feed');
    return;
  }

  // Allow access to onboarding and app routes
  next();
});

const { trackPageView } = useCustomerIo();

router.afterEach((to) => {
  trackPageView({
    name: to.name,
    path: to.path,
  });
});

CapApp.addListener('appStateChange', (state) => {
  if (state.isActive) {
    const current = router.currentRoute.value;

    trackPageView({
      name: current.name,
      path: current.path,
    });
  }
});

export default router;
